import { supabase } from '@/src/supabaseClient';
import { logger } from '@shared/logging/logger';
import { monitoringService } from '@shared/monitoring/monitoringService';

export type AuditSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface AuditEvent {
  id?: string;
  timestamp: string;
  userId: string | null;
  userEmail: string | null;
  role: string | null;
  action: string;
  category: 'authentication' | 'authorization' | 'resource_access' | 'session' | 'critical_operation';
  severity: AuditSeverity;
  status: 'success' | 'failure';
  resourceType?: string;
  resourceId?: string;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
}

class AuditLogger {
  private offlineBuffer: AuditEvent[] = [];
  private maxBufferSize = 100;

  constructor() {
    this.loadBuffer();
    
    // Listen to network transitions to purge offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.flushBuffer());
    }
  }

  /**
   * Main audit log dispatch. Handles local telemetry sinks and triggers cloud relays safely.
   */
  public async log(eventData: Omit<AuditEvent, 'timestamp'>): Promise<void> {
    const event: AuditEvent = {
      ...eventData,
      timestamp: new Date().toISOString(),
    };

    // 1. Dispatch to local enterprise logger
    const logMsg = `[AUDIT:${event.category.toUpperCase()}] ${event.action} - User: ${event.userEmail || 'Anonymous'} - Status: ${event.status.toUpperCase()}`;
    if (event.severity === 'critical' || (event.severity === 'high' && event.status === 'failure')) {
      logger.error('security_audit', logMsg, null, event);
      monitoringService.captureException('CRITICAL', new Error(`Security Audit Incident: ${event.action}`), 'fatal', event);
    } else if (event.severity === 'high' || event.severity === 'medium') {
      logger.warn('security_audit', logMsg, event);
    } else {
      logger.info('security_audit', logMsg, event);
    }

    // 2. Persist in cloud audit/logs db (Supabase) with fallback buffering to ensure safety
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        this.bufferEvent(event);
        return;
      }

      const { error } = await supabase
        .from('auditoria_seguranca')
        .insert({
          usuario_id: event.userId,
          usuario_email: event.userEmail,
          perfil: event.role,
          acao: event.action,
          categoria: event.category,
          gravidade: event.severity,
          resultado: event.status,
          tipo_recurso: event.resourceType,
          recurso_id: event.resourceId,
          user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
          detalhes: event.metadata,
          data_registro: event.timestamp // Mapping created_at -> data_registro per instruction
        });

      if (error) {
        // If the table 'auditoria_seguranca' doesn't exist or has schema mismatch,
        // we buffer and ignore standard SQL missing-table crashes.
        if (error.code === 'PGRST116' || error.message?.includes('does not exist')) {
          logger.debug('security_audit', 'Table auditoria_seguranca does not exist. Gracefully saving in offline buffer.');
        } else {
          logger.warn('security_audit', 'Failed syncing audit record to cloud', error);
        }
        this.bufferEvent(event);
      }
    } catch (err) {
      logger.debug('security_audit', 'Unexpected error logging schema fallback', err);
      this.bufferEvent(event);
    }
  }

  private bufferEvent(event: AuditEvent): void {
    this.offlineBuffer.push(event);
    if (this.offlineBuffer.length > this.maxBufferSize) {
      this.offlineBuffer.shift(); // Evict oldest
    }
    this.saveBuffer();
  }

  private loadBuffer(): void {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem('wastebank_audit_buffer');
      if (stored) {
        this.offlineBuffer = JSON.parse(stored);
      }
    } catch {
      this.offlineBuffer = [];
    }
  }

  private saveBuffer(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('wastebank_audit_buffer', JSON.stringify(this.offlineBuffer));
    } catch {}
  }

  /**
   * Safely flushes offline queued security audits once connection recovers.
   */
  private async flushBuffer(): Promise<void> {
    if (this.offlineBuffer.length === 0) return;
    
    logger.info('security_audit', `Restored connection. Flushing ${this.offlineBuffer.length} queued audit events...`);
    const eventsToFlush = [...this.offlineBuffer];
    this.offlineBuffer = [];
    this.saveBuffer();

    for (const event of eventsToFlush) {
      await this.log({
        userId: event.userId,
        userEmail: event.userEmail,
        role: event.role,
        action: event.action,
        category: event.category,
        severity: event.severity,
        status: event.status,
        resourceType: event.resourceType,
        resourceId: event.resourceId,
        metadata: event.metadata
      });
    }
  }
}

export const auditLogger = new AuditLogger();
export default auditLogger;
